import {
  PasswordlessAuthZeroClient,
  BaseAuthUser,
  DeliveryMethod,
  PasswordlessAuthZeroClientOptions,
  StorageClient,
  Credentials,
  AuthenticationDetails,
  PromiseOfOptional,
  AuthSession,
  AuthSessionClient,
  Optional,
} from '@quromedical/auth';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createTRPCProxyClient, httpLink } from '@trpc/client';
import { AuthRouter } from 'api';
import { Hub } from 'aws-amplify';
import { env } from 'config/env';

const storageClient: StorageClient = {
  getItem: async (key: string) =>
    AsyncStorage.getItem(key).then(value => {
      return value || undefined;
    }),
  setItem: async (key: string, value: string) => AsyncStorage.setItem(key, value),
  removeItem: async (key: string) => AsyncStorage.removeItem(key),
};

const client = createTRPCProxyClient<AuthRouter>({
  links: [httpLink({ url: `${env.trpcURL}/auth` })],
});

export type Session = AuthSession<BaseAuthUser>;
export class AuthApi<T extends BaseAuthUser> extends PasswordlessAuthZeroClient<T> {
  static async currentSession(): PromiseOfOptional<Session> {
    const session = (await AuthApi.instance?.getSession()) as Optional<Session>;

    return session;
  }

  static signOut() {
    return AuthApi.instance?.signOut();
  }

  static async getCurrentUser(): PromiseOfOptional<BaseAuthUser> {
    const user = (await AuthApi.instance?.getUser()) as Optional<BaseAuthUser>;

    return user;
  }

  static getInstance<T extends BaseAuthUser = BaseAuthUser>(
    options: PasswordlessAuthZeroClientOptions
  ): AuthApi<T> {
    if (!AuthApi.instance) {
      AuthApi.instance = new AuthApi(options, storageClient, AuthSessionClient, Hub);
    }

    return AuthApi.instance as AuthApi<T>;
  }

  async startAuthentication(
    mode: DeliveryMethod,
    username: string
  ): Promise<AuthenticationDetails> {
    return await client.authentication.start.mutate({
      mode,
      username,
    });
  }

  async completeAuthentication(
    mode: DeliveryMethod,
    username: string,
    code: string
  ): Promise<Credentials> {
    const data = await client.authentication.complete.mutate({
      mode,
      username,
      code,
    });

    await this.initializeSession(data);

    return data;
  }
}

export const authClient = AuthApi.getInstance({
  clientId: env.auth0ClientId,
  domain: env.auth0Domain,
});
